/**
 * Release gate that hands finalized utterances to the backend sync in open
 * order (runPrefix arrival, then chunkId).
 *
 * The backend transcript is an append-only string patched by char offsets, so
 * it must RECEIVE finals in the order they should read. Utterances can
 * finalize out of open order (a long-lived utterance on one lane closes after
 * a later-opened one on the other lane), so out-of-order finals are held here
 * until every earlier-opened utterance has finalized. The hold delays only
 * the backend write; the on-screen transcript renders from the projection and
 * is never held back.
 *
 * Liveness: the producer emits an utterance's first 'active' delta before any
 * delta of a later-opened utterance, so every id a final could wait on is
 * already known here. Held finals can therefore only wait on utterances that
 * genuinely exist, and they drain when those finalize, when the run dies
 * (a new run prefix appears), at meeting stop via `flushAll`, or (when the
 * native side wedges and none of those ever happen) when the hold deadline
 * expires via `releaseOverdueFinals`.
 */
import {
  utteranceRunPrefix,
  type NewlyFinalizedUtterance,
  type UtteranceTranscriptDelta,
} from './meetings-utterance-projection';

export type GatedFinalUtterance = {
  chunkId: number;
  runPrefix: string;
  speaker: string;
  text: string;
};

type PendingFinal = {
  final: GatedFinalUtterance;
  heldSinceMs: number;
};

type RunState = {
  openChunkIds: Set<number>;
  pendingByChunkId: Map<number, PendingFinal>;
  /**
   * Everything below this chunkId is resolved (finalized or declared dead by
   * an overdue release) and must never re-enter `openChunkIds`: a wedged
   * utterance can keep streaming hypothesis deltas after its forced drop, and
   * without the watermark each one would re-block all later finals.
   */
  resolvedBelowChunkId: number;
  flushed: boolean;
};

export type UtteranceSyncGate = {
  /**
   * Returns finals released as a side effect: a new run prefix flushes every
   * previous run.
   */
  observeDelta(delta: UtteranceTranscriptDelta): GatedFinalUtterance[];
  pushFinal(final: GatedFinalUtterance): GatedFinalUtterance[];
  /**
   * Safety valve for broken liveness: a final held longer than maxHoldMs can
   * only mean the utterances blocking it are dead (the producer caps an
   * utterance's lifetime, so legitimate holds are bounded well below it).
   * Drops those blockers and releases everything releasable, in order. A
   * blocker's final that still arrives later syncs immediately, just out of
   * read order in the backend transcript.
   */
  releaseOverdueFinals(maxHoldMs: number): GatedFinalUtterance[];
  /** Meeting stop: releases everything still held, in order. Idempotent. */
  flushAll(): GatedFinalUtterance[];
};

export function createUtteranceSyncGate(
  now: () => number = () => Date.now()
): UtteranceSyncGate {
  const runsByPrefix = new Map<string, RunState>();

  function ensureRun(runPrefix: string): {
    run: RunState;
    flushedFromOlderRuns: GatedFinalUtterance[];
  } {
    const existing = runsByPrefix.get(runPrefix);
    if (existing) {
      return { run: existing, flushedFromOlderRuns: [] };
    }
    // A new run prefix means the previous transcriber run is dead; nothing
    // more will finalize there, so holding its finals any longer is pure loss.
    const flushedFromOlderRuns = flushAllRuns();
    const run: RunState = {
      openChunkIds: new Set(),
      pendingByChunkId: new Map(),
      resolvedBelowChunkId: 0,
      flushed: false,
    };
    runsByPrefix.set(runPrefix, run);
    return { run, flushedFromOlderRuns };
  }

  function flushAllRuns(): GatedFinalUtterance[] {
    const released: GatedFinalUtterance[] = [];
    for (const run of runsByPrefix.values()) {
      released.push(...drainPendingInOrder(run.pendingByChunkId));
      run.openChunkIds.clear();
      run.flushed = true;
    }
    return released;
  }

  function releaseUnblockedPending(run: RunState): GatedFinalUtterance[] {
    const released: GatedFinalUtterance[] = [];
    let earliestOpen = minOf(run.openChunkIds);
    let earliestPending = minOf(run.pendingByChunkId.keys());
    while (
      earliestPending !== undefined &&
      (earliestOpen === undefined || earliestPending < earliestOpen)
    ) {
      const pending = run.pendingByChunkId.get(earliestPending);
      run.pendingByChunkId.delete(earliestPending);
      if (pending) released.push(pending.final);
      earliestOpen = minOf(run.openChunkIds);
      earliestPending = minOf(run.pendingByChunkId.keys());
    }
    return released;
  }

  return {
    observeDelta(delta) {
      const runPrefix = utteranceRunPrefix(delta.utteranceId);
      const { run, flushedFromOlderRuns } = ensureRun(runPrefix);
      if (
        !run.flushed &&
        !delta.isFinal &&
        delta.chunkId >= run.resolvedBelowChunkId &&
        !run.pendingByChunkId.has(delta.chunkId)
      ) {
        run.openChunkIds.add(delta.chunkId);
      }
      return flushedFromOlderRuns;
    },

    pushFinal(final) {
      const { run, flushedFromOlderRuns } = ensureRun(final.runPrefix);
      run.openChunkIds.delete(final.chunkId);
      if (run.flushed) {
        // The run already died (newer run took over); release straight away.
        return [...flushedFromOlderRuns, final];
      }
      run.pendingByChunkId.set(final.chunkId, { final, heldSinceMs: now() });
      return [...flushedFromOlderRuns, ...releaseUnblockedPending(run)];
    },

    releaseOverdueFinals(maxHoldMs) {
      const nowMs = now();
      const released: GatedFinalUtterance[] = [];
      for (const run of runsByPrefix.values()) {
        let newestOverdueChunkId: number | undefined;
        for (const [chunkId, pending] of run.pendingByChunkId) {
          if (nowMs - pending.heldSinceMs < maxHoldMs) continue;
          if (
            newestOverdueChunkId === undefined ||
            chunkId > newestOverdueChunkId
          ) {
            newestOverdueChunkId = chunkId;
          }
        }
        if (newestOverdueChunkId === undefined) continue;
        // Drop the dead blockers, not just the overdue finals: otherwise the
        // same wedged utterance re-blocks every subsequent final for another
        // full deadline. The watermark keeps them dropped when their deltas
        // keep arriving.
        for (const openChunkId of [...run.openChunkIds]) {
          if (openChunkId < newestOverdueChunkId) {
            run.openChunkIds.delete(openChunkId);
          }
        }
        run.resolvedBelowChunkId = Math.max(
          run.resolvedBelowChunkId,
          newestOverdueChunkId + 1
        );
        released.push(...releaseUnblockedPending(run));
      }
      return released;
    },

    flushAll() {
      return flushAllRuns();
    },
  };
}

export type UtteranceDeltaSink = {
  receive(delta: UtteranceTranscriptDelta): void;
  /** Meeting stop: syncs everything the gate still holds. Idempotent. */
  flush(): void;
};

/**
 * Upper bound on how long the gate may hold a final waiting for open order.
 * The producer hard-caps an utterance at 30s, so a hold beyond twice that can
 * only mean the blocking utterance is dead (wedged native lane, lost final).
 * Bounding the hold also bounds what an app crash can lose: held finals live
 * in renderer memory only until they reach the backend sync.
 */
const MAX_FINAL_HOLD_MS = 60_000;
const OVERDUE_SWEEP_INTERVAL_MS = 5_000;

/**
 * Wires the projection store and the backend sync together for one recording
 * session: every delta is observed by the gate before it is applied, finals
 * reported by `applyDelta` (at most once per utteranceId) enter the gate, and
 * everything the gate releases reaches `syncFinal` in open order, with one
 * exception: a dead blocker's final arriving after its forced overdue release
 * syncs immediately and out of read order (see `releaseOverdueFinals`).
 */
export function createUtteranceDeltaSink({
  applyDelta,
  syncFinal,
}: {
  applyDelta: (
    delta: UtteranceTranscriptDelta
  ) => NewlyFinalizedUtterance | undefined;
  syncFinal: (finalUtterance: GatedFinalUtterance) => void;
}): UtteranceDeltaSink {
  const gate = createUtteranceSyncGate();
  const syncAll = (finals: GatedFinalUtterance[]) => {
    for (const finalUtterance of finals) {
      syncFinal(finalUtterance);
    }
  };
  // A wedged pipeline delivers no further deltas, so the overdue sweep needs
  // its own timer; checking on the next delta would share the exact liveness
  // hole it exists to fix.
  const overdueSweepTimer = setInterval(() => {
    syncAll(gate.releaseOverdueFinals(MAX_FINAL_HOLD_MS));
  }, OVERDUE_SWEEP_INTERVAL_MS);

  return {
    receive(delta) {
      // Observe before apply: the gate must know the utterance is live before
      // its own final (reported by applyDelta) can be considered for release
      const releasedByRunChange = gate.observeDelta(delta);
      const newlyFinalized = applyDelta(delta);
      syncAll(releasedByRunChange);
      if (newlyFinalized) {
        syncAll(gate.pushFinal(newlyFinalized));
      }
    },
    flush() {
      clearInterval(overdueSweepTimer);
      syncAll(gate.flushAll());
    },
  };
}

function drainPendingInOrder(
  pendingByChunkId: Map<number, PendingFinal>
): GatedFinalUtterance[] {
  const drained = [...pendingByChunkId.values()]
    .sort((a, b) => a.final.chunkId - b.final.chunkId)
    .map((pending) => pending.final);
  pendingByChunkId.clear();
  return drained;
}

function minOf(values: Iterable<number>): number | undefined {
  let min: number | undefined;
  for (const value of values) {
    if (min === undefined || value < min) min = value;
  }
  return min;
}
